import React, { useEffect, useMemo } from 'react';
import { useFocusable, updateAllLayouts, FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import { getCMSImageURL } from '../../utils/api.utils';
import '../styles/nextContent.css';

/**
 * NextMusicCard Component
 * 
 * @param {Object} props
 * @param {Object} props.nextMusic - Next music data {slug, title, artist, coverImage}
 * @param {Function} props.onPlay - Callback when user clicks play or countdown reaches 0
 * @param {Function} props.onDismiss - Callback when user dismisses the card
 * @param {number} props.remainingSeconds - Seconds until auto-play
 * @param {boolean} props.visible - Whether card is visible
 */
function NextMusicCard(props) {
    var nextMusic = props.nextMusic;
    var onPlay = props.onPlay;
    var onDismiss = props.onDismiss;
    var remainingSeconds = props.remainingSeconds;
    var visible = props.visible;

    var { ref: cardRef, focused: cardFocused, focusKey } = useFocusable({
        focusKey: 'nextMusicCard',
        focusable: visible,
        preferredChildFocusKey: 'nextMusicPlayBtn',
    });

    var { ref: playBtnRef, focused: playFocused } = useFocusable({
        focusKey: 'nextMusicPlayBtn',
        focusable: visible,
        onEnterPress: function () {
            if (onPlay) onPlay();
        },
    });

    var { ref: dismissBtnRef, focused: dismissFocused } = useFocusable({
        focusKey: 'nextMusicDismissBtn',
        focusable: visible,
        onEnterPress: function () {
            if (onDismiss) onDismiss();
        },
    });

    // Auto-play when countdown reaches 0
    useEffect(function () {
        if (visible && remainingSeconds <= 0 && onPlay) {
            onPlay();
        }
    }, [visible, remainingSeconds, onPlay]);

    // Update spatial navigation tree when visibility changes
    useEffect(function () {
        updateAllLayouts();
    }, [visible]);

    // Memoize cover URL to prevent re-fetching on every render
    var coverUrl = useMemo(function () {
        if (nextMusic && nextMusic.coverImage) {
            return getCMSImageURL(nextMusic.coverImage, 'w300');
        }
        return '/placeholder-music.png';
    }, [nextMusic?.coverImage]);


    // Always render FocusContext.Provider, only hide content conditionally
    return React.createElement(FocusContext.Provider, { value: focusKey },
        (visible && nextMusic) ? React.createElement('div', {
            ref: cardRef,
            className: 'mnb-next-content-card ' + (cardFocused ? 'focused' : ''),
        },
            // Card Header
            React.createElement('div', { className: 'mnb-next-content-header' },
                React.createElement('h3', { className: 'mnb-next-content-title' }, 'موسیقی بعدی'),
                React.createElement('div', { className: 'mnb-next-content-timer' },
                    remainingSeconds > 0
                        ? React.createElement('span', null, remainingSeconds + ' ثانیه')
                        : React.createElement('span', null, 'در حال بارگذاری...')
                )
            ),

            // Card Content
            React.createElement('div', { className: 'mnb-next-content-body' },
                // Cover Image
                React.createElement('div', { className: 'mnb-next-content-image !w-40 !h-40 border-2 border-zinc-700' },
                    React.createElement('img', {
                        src: coverUrl,
                        alt: nextMusic.title || 'موسیقی بعدی',
                        onError: function (e) {
                            e.target.src = '/placeholder-music.png';
                        },
                    })
                ),

                // Music Info
                React.createElement('div', { className: 'mnb-next-content-info' },
                    React.createElement('h4', { className: 'mnb-next-music-title' },
                        nextMusic.title || 'بدون عنوان'
                    ),
                    nextMusic.titleEN && React.createElement('p', { className: 'mnb-next-music-artist' },
                        nextMusic.titleEN
                    )
                )
            ),

            // Action Buttons
            React.createElement('div', { className: 'mnb-next-content-actions' },
                React.createElement('button', {
                    ref: playBtnRef,
                    className: 'mnb-next-btn mnb-next-btn-primary ' + (playFocused ? 'focused' : ''),
                    onClick: onPlay,
                    type: 'button',
                }, 'پخش'),

                React.createElement('button', {
                    ref: dismissBtnRef,
                    className: 'mnb-next-btn mnb-next-btn-secondary ' + (dismissFocused ? 'focused' : ''),
                    onClick: onDismiss,
                    type: 'button',
                }, 'بستن')
            )
        ) : null
    );
}

export default NextMusicCard;
